/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { Button, Modal, Table } from 'react-bootstrap-v5'
import Swal from 'sweetalert2'
import { postRequestOptions } from '../helpers/Fetchwrapper'
import { formatDecimalQuantity } from '../helpers/GlobalHelper'

interface Props {
	/** blending order as returned by blending-orders/{id} */
	order: any;
	show: boolean;
	onHide: () => void;
	onSaved: () => void;
}

/**
 * Edit the actual production quantity of a QC passed blending order: it may go up, or down to the packed quantity.
 * The cost per unit becomes the material cost booked to WIP divided by the new quantity.
 */
function EditActualProductionQtyModal({ order, show, onHide, onSaved }: Props) {
	const [editQty, setEditQty] = useState('')
	const [editReason, setEditReason] = useState('')
	const [saving, setSaving] = useState(false)

	const packedQty = Number(order?.alreadyUsed ?? 0)
	const currentActualQty = Number(order?.effectiveProductionQuantity ?? 0)

	useEffect(() => {
		if (show) {
			setEditQty(formatDecimalQuantity(currentActualQty))
			setEditReason('')
		}
	}, [show])

	const editQtyNumber = Number(editQty)
	const editQtyError = editQty === '' || isNaN(editQtyNumber)
		? 'Enter the actual production quantity.'
		: editQtyNumber <= 0
			? 'Actual quantity must be greater than 0.'
			: editQtyNumber < packedQty - 0.00001
				? `Cannot be less than the packed quantity (${formatDecimalQuantity(packedQty)}).`
				: Math.abs(editQtyNumber - currentActualQty) < 0.00001
					? 'Quantity is unchanged.'
					: ''
	const bookedCost = Number(order?.bookedCost ?? 0)
	const newRatePerUnit = editQtyError === '' && bookedCost > 0 ? bookedCost / editQtyNumber : null

	const save = async () => {
		if (editQtyError !== '') {
			return
		}
		const confirm = await Swal.fire({
			title: 'Are you sure?',
			text: `Actual quantity will change from ${formatDecimalQuantity(currentActualQty, 2)} to ${formatDecimalQuantity(editQtyNumber, 2)}`
				+ (newRatePerUnit !== null
					? ` and cost per ${order.unitName} from ${formatDecimalQuantity(order.ratePerUnit, 4)} to ${formatDecimalQuantity(newRatePerUnit, 4)}`
					: '')
				+ '. The costs of its packing orders will be recalculated.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#ec008c',
			cancelButtonText: 'Cancel',
			confirmButtonText: 'Yes, Update',
		})
		if (!confirm.isConfirmed) {
			return
		}

		setSaving(true)
		try {
			const response = await fetch(
				`${process.env.REACT_APP_API_URL}blending-orders/update-actual-quantity/${order.idBlendingOrders}`,
				postRequestOptions({ actualProductionQuantity: editQtyNumber, reason: editReason.trim() })
			)
			const data = await response.json()
			if (data.success) {
				onHide()
				Swal.fire({ icon: 'success', title: 'Success', text: data.successMessage }).then(() => onSaved())
			} else {
				Swal.fire({ icon: 'error', title: 'Error', text: data.errorMessage || data.errors || 'Action failed.' })
			}
		} catch (error) {
			Swal.fire({ icon: 'error', title: 'Error', text: 'An error occurred.' })
		} finally {
			setSaving(false)
		}
	}

	return (
		<Modal show={show} onHide={() => !saving && onHide()}>
			<Modal.Header closeButton>
				<Modal.Title>Edit Actual Production Qty</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Table size="sm" bordered>
					<tbody>
						<tr>
							<td width="45%">Current Actual Qty</td>
							<td>{formatDecimalQuantity(currentActualQty, 2)} {order?.unitName}</td>
						</tr>
						<tr>
							<td>Packed Qty (minimum)</td>
							<td>{formatDecimalQuantity(packedQty)} {order?.unitName}</td>
						</tr>
						<tr>
							<td>Current Cost per {order?.unitName}</td>
							<td>{formatDecimalQuantity(order?.ratePerUnit, 4)}</td>
						</tr>
						<tr>
							<td>New Cost per {order?.unitName}</td>
							<td>{newRatePerUnit === null ? '-' : formatDecimalQuantity(newRatePerUnit, 4)}</td>
						</tr>
					</tbody>
				</Table>
				{order?.packingOrderCount > 0 &&
					<div className="text-muted fs-7 mb-4">
						{order.packingOrderCount} packing order(s) will be re-costed at the new cost per {order.unitName}.
					</div>
				}
				<div className="mb-4">
					<label className="form-label required">New Actual Qty ({order?.unitName})</label>
					<input
						type="text"
						inputMode="decimal"
						className={`form-control form-control-sm${editQty !== '' && editQtyError !== '' ? ' is-invalid' : ''}`}
						value={editQty}
						onChange={(event) => setEditQty(event.target.value)}
					/>
					{editQty !== '' && editQtyError !== '' && <div className="invalid-feedback">{editQtyError}</div>}
				</div>
				<div>
					<label className="form-label">Reason</label>
					<textarea
						className="form-control form-control-sm"
						rows={2}
						maxLength={500}
						value={editReason}
						onChange={(event) => setEditReason(event.target.value)}
					/>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" size="sm" disabled={saving} onClick={onHide}>Cancel</Button>
				<Button variant="primary" size="sm" disabled={saving || editQtyError !== ''} onClick={save}>
					{saving ? 'Saving...' : 'Update'}
				</Button>
			</Modal.Footer>
		</Modal>
	)
}

export default EditActualProductionQtyModal
